import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, Ticket, Sparkles, Settings, CreditCard, Receipt, CalendarClock, Download, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import { useLang } from "../../context/LanguageContext";
import { Reveal } from "../../components/site/Reveal";
import UpgradeCTA from "../../components/UpgradeCTA";
import ReferralPanel from "../../components/ReferralPanel";

export default function Dashboard() {
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [renewCycle, setRenewCycle] = useState(null);
  const [renewing, setRenewing] = useState(false);

  const load = () => {
    setError(false);
    api.get("/member/dashboard").then((r) => setData(r.data)).catch(() => setError(true));
  };
  useEffect(() => { load(); }, []);

  const u = data?.user;
  const b = data?.billing;
  const showRenew = b && !b.is_free && (b.expires_soon || b.is_overdue);
  const activeCycle = renewCycle || b?.billing_cycle || "monthly";

  const renewNow = async () => {
    setRenewing(true);
    try {
      const { data: res } = await api.post("/member/renew", {
        origin_url: window.location.origin,
        billing_cycle: activeCycle,
      });
      if (res.checkout_url) window.location.href = res.checkout_url;
      else load();
    } catch (e) {
      setRenewing(false);
    }
  };
  const stats = [
    { Icon: Ticket, label: t("Events RSVP'd", "Événements réservés"), value: data?.upcoming_count ?? 0 },
    { Icon: Users, label: t("Network size", "Taille du réseau"), value: data?.network_size ?? 0 },
    { Icon: Sparkles, label: t("Membership", "Adhésion"), value: u?.tier || "—" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main data-testid="member-dashboard" className="flex-1 pt-28 pb-20 max-w-7xl mx-auto px-5 md:px-8 w-full">
        {error ? (
          <div className="bg-card border border-primary/10 rounded-3xl p-10 text-center">
            <p className="text-foreground/70">{t("We couldn't load your dashboard.", "Impossible de charger votre tableau de bord.")}</p>
            <button onClick={load} data-testid="dashboard-retry" className="mt-5 rounded-full bg-primary text-primary-foreground px-7 py-3 font-semibold hover:bg-secondary transition-colors">{t("Try again", "Réessayer")}</button>
          </div>
        ) : !data ? (
          <div className="min-h-[40vh] flex items-center justify-center font-display text-3xl text-primary/40 animate-pulse">GYBC</div>
        ) : (
        <>
        <Reveal>
          <div className="bg-primary text-primary-foreground rounded-[2rem] p-8 md:p-12 grain">
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">{t("Welcome back", "Bon retour")}</p>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-2">{t("Hi", "Bonjour")}, {u?.name?.split(" ")[0] || t("friend", "amie")}.</h1>
            <p className="text-primary-foreground/70 mt-2 capitalize">{t("Your", "Votre")} {u?.tier?.replace("_", " ")} {t("membership", "adhésion")} · {u?.membership_status}{u?.renews_at ? ` · ${t("renews", "renouvellement")} ${String(u.renews_at).slice(0, 10)}` : ""}</p>
            <Link to="/account" data-testid="dashboard-account-link" className="inline-flex items-center gap-2 mt-5 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20 px-5 py-2.5 text-sm font-semibold transition-colors">
              <Settings size={15} /> {t("Account settings", "Paramètres du compte")}
            </Link>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-3 gap-6 mt-8">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="bg-card border border-primary/10 rounded-3xl p-7">
                <div className="w-11 h-11 rounded-full bg-secondary/15 text-secondary flex items-center justify-center mb-4"><s.Icon size={20} /></div>
                <p className="font-display text-4xl font-semibold text-primary capitalize">{s.value}</p>
                <p className="text-muted-foreground mt-1">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {showRenew && (
          <Reveal>
            <div data-testid="renew-panel" className={`mt-8 rounded-3xl p-7 md:p-8 border ${b.is_overdue ? "bg-destructive/5 border-destructive/30" : "bg-secondary/5 border-secondary/30"}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center ${b.is_overdue ? "bg-destructive/15 text-destructive" : "bg-secondary/15 text-secondary"}`}><AlertTriangle size={22} /></div>
                  <div>
                    <h2 className="font-display text-2xl font-semibold text-primary">
                      {b.is_overdue
                        ? t("Your membership is overdue", "Votre adhésion est en retard")
                        : t("Your membership is expiring soon", "Votre adhésion expire bientôt")}
                    </h2>
                    <p className="text-sm text-foreground/70 mt-1">
                      {b.is_overdue
                        ? t("Renew now to keep your access — it will be suspended once the grace period ends.", "Renouvelez maintenant pour conserver votre accès — il sera suspendu à la fin du délai de grâce.")
                        : t(`Renews on ${String(b.renews_at).slice(0, 10)}${typeof b.days_until_renewal === "number" ? ` · ${b.days_until_renewal} day(s) left` : ""}. Renew early to stay in the circle.`, `Renouvellement le ${String(b.renews_at).slice(0, 10)}. Renouvelez tôt pour rester dans le cercle.`)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                  <div className="inline-flex rounded-full border border-primary/15 p-1 bg-background">
                    <button type="button" data-testid="renew-cycle-monthly" onClick={() => setRenewCycle("monthly")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${activeCycle === "monthly" ? "bg-primary text-primary-foreground" : "text-primary/70"}`}>{t("Monthly", "Mensuel")}</button>
                    <button type="button" data-testid="renew-cycle-yearly" onClick={() => setRenewCycle("yearly")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${activeCycle === "yearly" ? "bg-primary text-primary-foreground" : "text-primary/70"}`}>{t("Yearly", "Annuel")}</button>
                  </div>
                  <button onClick={renewNow} disabled={renewing} data-testid="renew-now-btn" className="rounded-full bg-secondary text-secondary-foreground px-7 py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
                    {renewing ? t("Opening checkout…", "Ouverture du paiement…") : t("Renew now", "Renouveler")}
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        )}

        <div className="mt-8"><Reveal><UpgradeCTA variant="card" /></Reveal></div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <Reveal>
            <div data-testid="billing-card" className="bg-card border border-primary/10 rounded-3xl p-7 h-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><CreditCard size={20} /></div>
                <h2 className="font-display text-2xl font-semibold text-primary">{t("Membership & billing", "Adhésion et facturation")}</h2>
              </div>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-muted-foreground">{t("Plan", "Forfait")}</dt><dd className="font-semibold text-primary capitalize">{data?.billing?.tier_name || u?.tier}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">{t("Status", "Statut")}</dt><dd className="font-semibold text-primary capitalize">{data?.billing?.status}</dd></div>
                {data?.billing?.is_free ? (
                  <div className="flex justify-between"><dt className="text-muted-foreground">{t("Price", "Prix")}</dt><dd className="font-semibold text-primary">{t("Free", "Gratuit")}</dd></div>
                ) : (
                  <>
                    <div className="flex justify-between"><dt className="text-muted-foreground">{t("Billing", "Facturation")}</dt><dd className="font-semibold text-primary">${Number(data?.billing?.amount || 0).toFixed(2)} {data?.billing?.currency} / {data?.billing?.billing_cycle === "yearly" ? t("year", "an") : t("month", "mois")}</dd></div>
                    <div className="flex justify-between items-center"><dt className="text-muted-foreground flex items-center gap-1.5"><CalendarClock size={14} className="text-secondary" /> {t("Next renewal", "Prochain renouvellement")}</dt><dd className="font-semibold text-primary" data-testid="billing-renews">{data?.billing?.renews_at ? String(data.billing.renews_at).slice(0, 10) : "—"}</dd></div>
                  </>
                )}
              </dl>
              <Link to="/account" className="inline-flex items-center gap-2 mt-6 rounded-full border border-primary/20 text-primary px-5 py-2.5 text-sm font-semibold hover:bg-primary/5 transition-colors"><Settings size={15} /> {t("Manage membership", "Gérer l'adhésion")}</Link>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div data-testid="receipts-card" className="bg-card border border-primary/10 rounded-3xl p-7 h-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><Receipt size={20} /></div>
                <h2 className="font-display text-2xl font-semibold text-primary">{t("Payment receipts", "Reçus de paiement")}</h2>
              </div>
              {data?.receipts?.length ? (
                <ul className="divide-y divide-primary/10">
                  {data.receipts.map((r) => (
                    <li key={r.id} data-testid={`receipt-${r.id}`} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="font-medium text-primary truncate">{r.description}</p>
                        <p className="text-xs text-muted-foreground">{r.date} · ${Number(r.amount || 0).toFixed(2)} {r.currency}</p>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/member/receipt/${r.id}`} target="_blank" rel="noreferrer" data-testid={`receipt-view-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3.5 py-2 text-xs font-semibold hover:bg-primary/20 transition-colors"><Receipt size={13} /> {t("View", "Voir")}</a>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/member/receipt/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`receipt-pdf-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-secondary text-secondary-foreground px-3.5 py-2 text-xs font-semibold hover:opacity-90 transition-colors"><Download size={13} /> {t("PDF", "PDF")}</a>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">{t("No payments yet. Your receipts will appear here after your first payment.", "Aucun paiement. Vos reçus apparaîtront ici après votre premier paiement.")}</p>
              )}
            </div>
          </Reveal>
        </div>

        <div className="mt-8"><Reveal><ReferralPanel /></Reveal></div>

        <div className="mt-12">
          <h2 className="font-display text-3xl font-medium text-primary mb-6">{t("Your upcoming events", "Vos prochains événements")}</h2>
          {data?.rsvp_events?.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.rsvp_events.map((ev) => (
                <Link key={ev.id} to={`/events/${ev.id}`} className="bg-card border border-primary/10 rounded-3xl overflow-hidden group hover:-translate-y-1 transition-transform">
                  <div className="aspect-[16/10] overflow-hidden"><img src={ev.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-semibold text-primary">{lang === "fr" ? ev.title_fr : ev.title_en}</h3>
                    <p className="flex items-center gap-2 text-sm text-primary/70 mt-2"><Calendar size={14} className="text-secondary" /> {ev.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-card border border-primary/10 rounded-3xl p-10 text-center">
              <p className="text-foreground/70">{t("You haven't RSVP'd to any events yet.", "Vous n'êtes inscrite à aucun événement.")}</p>
              <Link to="/events" className="inline-block mt-5 rounded-full bg-primary text-primary-foreground px-7 py-3 font-semibold hover:bg-secondary transition-colors">{t("Browse events", "Voir les événements")}</Link>
            </div>
          )}
        </div>
        </>
        )}
      </main>
      <Footer />
    </div>
  );
}
